import React, { useState, useEffect, useCallback } from 'react'
import { Toaster } from 'sonner'
import { ShieldX } from 'lucide-react'
import { setToken, getToken } from './utils/api'
import FileSidebar from './components/FileSidebar'
import FileBrowser from './components/FileBrowser'
import FileViewer from './components/FileViewer'


function AccessDenied({ reason }) {
  return (
    <div className="h-screen flex items-center justify-center">
      <div className="max-w-sm w-full text-center space-y-4">
        <div className="w-16 h-16 rounded-2xl bg-red-50 border border-red-100 flex items-center justify-center mx-auto">
          <ShieldX size={28} className="text-red-500" />
        </div>
        <div>
          <h1 className="text-sm font-bold text-gray-900 mb-1">
            {reason === 'expired' ? 'Session Expired' : 'Forbidden Access'}
          </h1>
          <p className="text-xs text-gray-500 leading-relaxed">
            {reason === 'expired'
              ? 'Your session has expired or is invalid. Return to the panel and reopen the File Manager from the website\'s Files tab.'
              : 'Access is forbidden. This file manager must be opened directly from the Brainwave Servers Panel — it cannot be accessed standalone.'}
          </p>
        </div>
        <p className="text-[11px] text-gray-400">Brainwave Servers — File Manager</p>
      </div>
    </div>
  )
}

function updateUrl(patches) {
  const url = new URL(window.location.href)
  Object.entries(patches).forEach(([k, v]) => {
    if (v == null) url.searchParams.delete(k)
    else url.searchParams.set(k, v)
  })
  window.history.pushState({}, '', url.toString())
}

export default function App() {
  const [domain,      setDomain]      = useState(null)
  const [ready,       setReady]       = useState(null) // null=loading, true=ok, 'expired'|'forbidden'=blocked
  const [currentPath, setCurrentPath] = useState('/')
  const [activeFile,  setActiveFile]  = useState(null)
  const [viewerOpen,  setViewerOpen]  = useState(false)

  useEffect(() => {
    const params    = new URLSearchParams(window.location.search)
    const urlToken  = params.get('token')
    const urlDomain = params.get('domain')
    const urlPath   = params.get('path')
    const urlFile   = params.get('file')
    const urlFName  = params.get('filename')

    if (urlToken) {
      setToken(urlToken)
      const clean = new URL(window.location.href)
      clean.searchParams.delete('token')
      window.history.replaceState({}, '', clean.toString())
    }

    const finalToken  = urlToken || getToken()
    const finalDomain = urlDomain

    if (!finalToken) {
      setReady('expired')
    } else if (!finalDomain) {
      setReady('forbidden')
    } else {
      setDomain(finalDomain)
      setReady(true)
      if (urlPath)  setCurrentPath(urlPath)
      if (urlFile) {
        const fileObj = { path: urlFile, name: urlFName || urlFile.split('/').pop() }
        setActiveFile(fileObj)
        setViewerOpen(true)
      }
    }
  }, [])

  useEffect(() => {
    const handle = () => setReady('expired')
    window.addEventListener('fm_unauthorized', handle)
    return () => window.removeEventListener('fm_unauthorized', handle)
  }, [])

  // Browser back/forward
  useEffect(() => {
    const onPop = () => {
      const params  = new URLSearchParams(window.location.search)
      const urlPath = params.get('path')
      const urlFile = params.get('file')
      const urlFName = params.get('filename')
      if (urlFile) {
        const fileObj = { path: urlFile, name: urlFName || urlFile.split('/').pop() }
        setActiveFile(fileObj)
        setViewerOpen(true)
      } else {
        setActiveFile(null)
        setViewerOpen(false)
      }
      if (urlPath) setCurrentPath(urlPath)
    }
    window.addEventListener('popstate', onPop)
    return () => window.removeEventListener('popstate', onPop)
  }, [])

  const handleNavigate = useCallback((path) => {
    setCurrentPath(path)
    setActiveFile(null)
    setViewerOpen(false)
    updateUrl({ path, file: null, filename: null })
  }, [])

  const openFile = useCallback((file) => {
    setActiveFile(file)
    setViewerOpen(!!file)
    if (file) updateUrl({ file: file.path, filename: file.name })
    else      updateUrl({ file: null, filename: null })
  }, [])

  const closeFile = useCallback(() => {
    setViewerOpen(false)
    setActiveFile(null)
    updateUrl({ file: null, filename: null })
  }, [])

  // Still initialising
  if (ready === null) return null

  // Access denied states
  if (ready === 'expired')   return <AccessDenied reason="expired" />
  if (ready === 'forbidden') return <AccessDenied reason="forbidden" />

  return (
    <>
      <Toaster position="top-right" richColors />

      <div className="flex h-screen overflow-hidden bg-ink">
        {/* Sidebar — shows directory tree normally, file details when a file is open */}
        <FileSidebar
          domain={domain}
          currentPath={currentPath}
          onNavigate={handleNavigate}
          activeFile={activeFile}
          onCloseFile={closeFile}
        />

        {/* Floating white content panel, matching the main panel's shell */}
        <div className="relative z-10 m-0 flex min-w-0 flex-1 flex-col overflow-hidden bg-white shadow-sm lg:m-3 lg:ml-0 lg:rounded-2xl">
          {viewerOpen ? (
            <FileViewer
              domain={domain}
              file={activeFile}
              onClose={closeFile}
              onExtracted={(destination) => { closeFile(); handleNavigate(destination) }}
            />
          ) : (
            <FileBrowser
              domain={domain}
              path={currentPath}
              onNavigate={handleNavigate}
              onOpenFile={openFile}
              activeFile={activeFile}
            />
          )}
        </div>
      </div>
    </>
  )
}
